// @ts-nocheck
/* eslint-disable @typescript-eslint/no-empty-object-type */

import { createTheme, Theme, PaletteOptions } from '@mui/material/styles';

import { darkModeTheme } from './theme_definitions/darkModeTheme';
import { lightModeTheme } from './theme_definitions/lightModeTheme';
import { colorBlindModeTheme } from './theme_definitions/colorBlindModeTheme';
import { EquityColumnGroup } from '../types';
import { OCCCategory } from '../types/sentiment';
import merge from 'lodash/merge';

export enum ColorMode {
  DARK = 'Dark',
  LIGHT = 'Light',
  COLOR_BLIND = 'Color Blind Friendly',
}

export type HeatmapColorSettings = {
  hiroColor: string;
  candleUpFillColor: string;
  candleDownFillColor: string;
  candleUpLineColor: string;
  candleDownLineColor: string;
  contourLineColor: string;
  callColor: string;
  putColor: string;
  chartZeroColor: string;
  lastCandleColor: string;
  posHeatmapColor: string;
  negHeatmapColor: string;
  posStrikeColor: string;
  negStrikeColor: string;
};

export type StrikeBarColorSettings = {
  tracker: {
    last: string;
    thirtyMin: string;
    hour: string;
    dailyMax: string;
    dailyMin: string;
  };
};

export type TraceColorSettings = {
  heatmapSettings: HeatmapColorSettings;
  strikeBarSettings: StrikeBarColorSettings;
  updateStatusColor: string;
};

export type CompassColorSettings = {
  freeformBg: string;
  buttonColor: string;
  editChipBgColor: string;
  disabledChipBgColor: string;
  pointColor: string;
  historyPalette: [number, number, number][];
};

type CustomPaletteKeys = {
  shadows: {
    default: string;
    paperBoxShadow: string;
  };
  sgGreen: string;
  gray: string;
  core: {
    price: string;
    put: string;
    call: string;
    translucentBG: string;
  };
  button: {
    default: string;
    hover: string;
  };
  equityHub: {
    fieldColorMapping: {
      // must match FIELD_NAME_MAPPING keys
      upx: string;
      maxfs: string;
      keyg: string;
      keyd: string;
      cws: string;
      pws: string;
      putctrl: string;
      high_vol_point: string;
      low_vol_point: string;
    };
    composite: {
      neutral: string;
    };
    skew: {
      nextExpFirst: string;
      nextExpSecond: string;
      allFirst: string;
      allSecond: string;
    };
    putCallImpact: {
      nextExpGex: string;
      allExpGex: string;
    };
    synthOILines: {
      curve_gamma_all: string;
      curve_gamma_next_exp: string;
      curve_gamma_monthly: string;
      curve_gamma_all_sans_ne: string;
      curve_gamma_all_sans_monthly: string;
      curve_delta_all: string;
      curve_delta_next_exp: string;
      curve_delta_monthly: string;
      curve_delta_all_sans_ne: string;
      curve_delta_all_sans_monthly: string;
    };
    eh_live_price: {
      callColor: string;
      putColor: string;
      posGexColor: string;
      negGexColor: string;
    };
  };
  equityHubColumns: {
    [EquityColumnGroup.STATISTICS]: string;
    [EquityColumnGroup.SG_KEY_DAILY_LEVELS]: string;
    [EquityColumnGroup.DIRECTIONAL_INDICATORS]: string;
    [EquityColumnGroup.VOLATILITY_INSIGHTS]: string;
    [EquityColumnGroup.DARK_POOL_INDICATORS]: string;
  };
  scannerGrid: {
    mainHeader: string;
    pinnedRow: string;
    pinnedColumn: string;
  };
  alerts: {
    hiroReversionTop: string;
    hiroReversionBottom: string;
  };

  hiro: {
    signalBg: string;
    flow: string;
    indices: {
      SPY: string;
      QQQ: string;
      IWM: string;
    };
    bottomCandles: {
      absolute: string;
    };
    lenses: {
      all: ChartLenseColorConfig;
      retail: ChartLenseColorConfig;
      nextExp: ChartLenseColorConfig;
      price: {
        total: string;
      };
    };
    priceLines: {
      upx: string;
      pws: string;
      keyg: string;
      keyd: string;
      cws: string;
      maxfs: string;
    };
    grid: {
      lineColor: string;
    };
  };
  sentiment: {
    occ: {
      [OCCCategory.EQUITY_CALLS]: string;
      [OCCCategory.EQUITY_PUTS]: string;
      [OCCCategory.ETF_CALLS]: string;
      [OCCCategory.ETF_PUTS]: string;
      [OCCCategory.INDEX_CALLS]: string;
      [OCCCategory.INDEX_PUTS]: string;
    };
    historical: {
      upx: string;
      max_g_strike: string;
      put_wall_strike: string;
      call_wall_strike: string;
      gamma_index: string;
    };
  };
  indices: {
    combos: {
      gamma: string;
    };
    delta: {
      all: string;
      nextExpMonthly: string;
      nextExp: string;
    };
    gammaVanna: {
      gamma: string;
      vanna: string;
      delta: string;
      volforecast: string;
      nextExp: string;
    };
    tilt: {
      upx: string;
      gamma: string;
      delta: string;
    };
    zeroDte: {
      volume: string;
      oi: string;
      oi_avg: string;
    };
    equityRatio: {
      volume: string;
      oi: string;
    };
    vol: {
      five_day_vol: string;
      one_month_vol: string;
      two_month_vol: string;
      three_month_vol: string;
      six_month_vol: string;
    };
    maxVol: {
      price: string;
      two_six_month: string;
      one_three_month: string;
    };
  };
  iVol: {
    termStructure: {
      [date: string]: string;
    };
    volSkew: {
      [date: string]: string;
    };
    fixedStrikeMatrix: {
      grayCell: string;
      lowValueCell: string;
      highValueCell: string;
      highlight: string;
    };
  };
  resources: {
    earnings: {
      bmo: string;
      amc: string;
      other: string;
      label: string;
    };
  };
  trace: TraceColorSettings;
  compass: CompassColorSettings;
};

declare module '@mui/material/styles' {
  interface Theme {
    colorMode: ColorMode;
    status: {
      danger: string;
    };
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    colorMode: ColorMode;
    status?: {
      danger?: string;
    };
  }

  interface Palette extends CustomPaletteKeys {}
  interface PaletteOptions extends CustomPaletteKeys {}
}

type ChartLenseColorConfig = {
  call: string;
  put: string;
  total: string;
};

type CustomColorModeDefinition = {
  palette: PaletteOptions;
};

const definitionForColorMode = (
  colorMode: ColorMode,
): CustomColorModeDefinition => {
  switch (colorMode) {
    case ColorMode.LIGHT:
      return lightModeTheme;
    case ColorMode.COLOR_BLIND:
      return colorBlindModeTheme;
    case ColorMode.DARK:
    default:
      return darkModeTheme;
  }
};

export const themeForMode = (colorMode: ColorMode): Theme => {
  const definition = merge(definitionForColorMode(colorMode), {
    palette: {
      compass: {
        historyPalette: minimallyConflictingLineHSL,
      },
      hiro: {
        minimallyConflictingLineHSL,
      },
    },
  });

  return createTheme({
    colorMode: colorMode,
    spacing: [0, 2, 4, 8, 12, 14, 16, 24, 32, 64],
    palette: {
      primary: {
        main: '#18bdac',
      },
      secondary: {
        main: '#6467E8',
      },
      success: {
        main: '#22CC63',
      },
      error: {
        main: '#FF5E5E',
      },
      warning: {
        main: '#ffcd5e',
      },
      info: {
        main: '#878787',
      },
      divider: '#0f1214',
      ...definition.palette,
    },
    typography: {
      fontSize: 12,
      fontWeightLight: 400,
      body1: {
        color: definition.palette.text!.primary,
        display: 'inline',
      },
      body2: {
        color: '#636569',
        display: 'inline',
      },
      h1: {
        fontSize: 24,
        fontWeight: 500,
        fontFamily: 'Satoshi Complete',
      },
      fontFamily: 'SF Pro Display',
      h2: {
        fontSize: 16,
        fontFamily: 'Satoshi Complete',
      },
      h3: {
        fontSize: 14,
        fontFamily: 'Satoshi Complete',
      },
      h4: {
        fontSize: 12,
      },
      h5: {
        fontSize: 11,
      },
      button: {
        fontSize: 14,
        fontWeight: 500,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          // Name of the slot
          root: {
            // Some CSS
            fontSize: '1rem',
            ':disabled': {
              color: definition.palette.text?.disabled,
            },
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          root: {
            borderRadius: 3,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: '#0e7167',
          },
          label: {
            fontSize: '12px',
            color: '#ffffff',
          },
          deleteIcon: {
            color: 'rgba(255,255,255,0.25)',
          },
        },
      },
    },
  });
};

// https://medialab.github.io/iwanthue/
const minimallyConflictingLineHSL = [
  [243, 52, 68],
  [103, 60, 29],
  [279, 74, 53],
  [145, 49, 35],
  [28, 41, 35],
  [295, 61, 44],
  [66, 55, 28],
  [260, 70, 35],
  [106, 32, 33],
  [240, 62, 58],
  [10, 77, 45],
  [241, 45, 42],
  [42, 66, 31],
  [270, 51, 52],
  [93, 48, 19],
  [290, 37, 47],
  [159, 34, 34],
  [259, 64, 27],
  [23, 66, 38],
  [239, 40, 55],
  [11, 68, 32],
  [215, 54, 45],
  [26, 50, 20],
  [290, 54, 32],
  [151, 27, 23],
  [254, 77, 18],
  [48, 16, 36],
  [282, 61, 21],
  [190, 32, 37],
  [296, 69, 14],
  [88, 25, 13],
  [245, 35, 37],
  [214, 47, 10],
  [278, 24, 45],
  [282, 48, 8],
  [216, 30, 46],
  [278, 80, 10],
  [249, 9, 42],
  [249, 64, 15],
  [198, 40, 26],
  [242, 46, 25],
  [201, 35, 17],
  [289, 33, 26],
  [242, 54, 11],
  [224, 30, 36],
  [280, 24, 15],
  [222, 43, 23],
  [276, 46, 16],
  [278, 15, 25],
  [240, 35, 17],
];

// https://maketintsandshades.com/#18bdac
